// src/services/dataPersistence/fullDataSyncHandler.ts
import { AutoSyncService, SyncState } from './autoSyncService';
import { dataSyncService } from './index';
import { TripperUserData } from './types';
import { itineraryApiService } from './itineraryApi'; // Use existing import
import transformers from './transformers';
import { authTokenManager } from '../../utils/authTokenManager';


// Import API services for data types
import { 
  conversationApiService, 
  locationApiService, 
  weatherApiService, 
  imageApiService,
  preferencesApiService,
  mapStateApiService
} from '../api/dataApiServices';

// Create and initialize the AutoSyncService
const autoSyncService = AutoSyncService.getInstance();
autoSyncService.initialize({ syncIntervalMs: 3000 }); // 30 seconds

/**
 * Full data sync handler with comprehensive interface
 */
class FullDataSyncHandlerClass {
  private syncInProgress: boolean = false;
  private lastSyncTime: number = 0;
  private syncStatus: Record<string, { status: string, time: number, error?: string }> = {};

  // Track last sync time per data type
  private lastSyncTimes: Record<string, number> = {};
  
  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Load sync times from storage
    this.loadSyncTimes();
    
    // Set up AutoSyncService to use our detailed sync implementation
    // This requires modifying AutoSyncService to accept a custom sync function
    if (autoSyncService.setCustomSyncFunction) {
      autoSyncService.setCustomSyncFunction(this.syncAllData.bind(this));
    }
  }
  
  private handleOnline() {
    //console.log('[fullDataSyncHandler] Device came online, triggering sync');
    this.initializeFromServer();
    this.syncAllData();
  }
  
  private handleOffline() {
    //console.log('[fullDataSyncHandler] Device went offline');
    //this.updateSyncStatus('all', 'offline', 'Device is offline');
  }
  
  // Check if data has changed since last sync
  private hasDataChanged(dataType: string, data: any): boolean {
    // Get the last sync time for this data type
    const lastSync = this.lastSyncTimes[dataType] || 0;
    
    // Check if data has lastUpdated timestamp
    const lastUpdated = data?.lastUpdatedAt || data?.timestamp || 0;
    
    // If no timestamp, check if it's a collection with timestamps
    if (!lastUpdated && Array.isArray(data)) {
      // Find the most recent update in the collection
      const mostRecentUpdate = data.reduce((maxTime, item) => {
        const itemTime = item?.lastUpdatedAt || item?.timestamp || 0;
        return Math.max(maxTime, typeof itemTime === 'string' ? new Date(itemTime).getTime() : itemTime);
      }, 0);
      
      //console.log(`[fullDataSyncHandler] ${dataType} collection last updated: ${new Date(mostRecentUpdate).toISOString()}, last sync: ${new Date(lastSync).toISOString()}`);
      
      return mostRecentUpdate > lastSync;
    }
    
    // Convert string dates to timestamps if needed
    const updateTime = typeof lastUpdated === 'string' ? new Date(lastUpdated).getTime() : lastUpdated;
    
    //console.log(`[fullDataSyncHandler] ${dataType} last updated: ${new Date(updateTime).toISOString()}, last sync: ${new Date(lastSync).toISOString()}`);
    
    // Only sync if data was updated after last sync
    return updateTime > lastSync;
  }

  // Update the lastSyncTimes record after successful sync
  private updateLastSyncTime(dataType: string): void {
    this.lastSyncTimes[dataType] = Date.now();
    
    // Also persist to localStorage for persistence across sessions
    try {
      const syncTimes = JSON.parse(localStorage.getItem('tripper_sync_times') || '{}');
      syncTimes[dataType] = this.lastSyncTimes[dataType];
      localStorage.setItem('tripper_sync_times', JSON.stringify(syncTimes));
    } catch (e) {
      //console.error('[fullDataSyncHandler] Error saving sync times:', e);
    }
  }

  // Load sync times from storage on initialization
  private loadSyncTimes(): void {
    try {
      const syncTimes = JSON.parse(localStorage.getItem('tripper_sync_times') || '{}');
      this.lastSyncTimes = syncTimes;
      //console.log('[fullDataSyncHandler] Loaded sync times:', this.lastSyncTimes);
    } catch (e) {
      //console.error('[fullDataSyncHandler] Error loading sync times:', e);
    }
  }

  /**
   * Update sync status for a specific data type
   */
  private updateSyncStatus(
    dataType: string, 
    status: string, 
    error?: string
  ): void {
    this.syncStatus[dataType] = {
      status,
      time: Date.now(),
      ...(error ? { error } : {})
    };
    
    // Determine overall status
    let overall: 'idle' | 'syncing' | 'error' | 'success' | 'offline' = 'idle';
    
    if (status === 'offline') {
      overall = 'offline';
    } else if (this.syncInProgress) {
      overall = 'syncing';
    } else if (Object.values(this.syncStatus).some(s => s.status === 'error')) {
      overall = 'error';
    } else if (Object.keys(this.syncStatus).length > 0) {
      overall = 'success';
    }
    
    // Create sync state object
    const syncState: SyncState = {
      status: overall === 'offline' ? 'error' : overall as any,
      lastSyncTime: this.lastSyncTime,
      error: error || null
    };
    
    // Notify listeners
    window.dispatchEvent(new CustomEvent('tripper-sync-status-updated', {
      detail: syncState
    }));
  }
  

  // In fullDataSyncHandler.ts
  // Fix 4: Update the initializeFromServer method to ALWAYS prioritize server data
  public async initializeFromServer(): Promise<boolean> {
    console.log('[fullDataSyncHandler] Initializing data from server...');
    let dataImported = false;
    
    try {
      // Set a flag in localStorage to indicate server data fetch is in progress
      localStorage.setItem('server_data_fetch_in_progress', 'true');
      
      // 1. Fetch all data types from server FIRST - server is single source of truth
      const [serverConversation, serverLocations, serverImages, serverWeather, serverMapState] = 
        await Promise.all([
          this.fetchFromServer('/api/user-data/conversation'),
          this.fetchFromServer('/api/user-data/locations'),
          this.fetchFromServer('/api/user-data/images'),
          this.fetchFromServer('/api/user-data/weather'),
          this.fetchFromServer('/api/user-data/mapstate')
        ]);
      
      // 2. Get current local data AFTER fetching from server
      const localData = dataSyncService.getAllData();
      const hasLocalData = localData && 
                          localData.conversation?.sequence?.length > 0;
      
      console.log('[fullDataSyncHandler] Data comparison:', {
        hasLocalData,
        localConversationCount: localData.conversation?.sequence?.length || 0,
        serverConversationCount: serverConversation?.sequence?.length || 0,
        serverHasData: !!serverConversation?.sequence?.length
      });
      
      // 3. CRITICAL: For conversation data, SERVER IS SOURCE OF TRUTH
      if (serverConversation && serverConversation.sequence && serverConversation.sequence.length > 0) {
        console.log(`[fullDataSyncHandler] Server has ${serverConversation.sequence.length} conversation elements - ALWAYS IMPORTING`);
        
        // Always use server data if it exists, regardless of local data
        const transformedData = transformers.conversationApiToClient(serverConversation);
        
        if (transformedData) {
          // Update local storage with server data
          dataSyncService.updateConversationData(transformedData);
          dataImported = true;
          //console.log('[fullDataSyncHandler] Successfully imported server conversation data');
        } else {
          //console.warn('[fullDataSyncHandler] Conversation data transformation returned null/undefined');
        }
      } else {
        // Only if server has NO data, then we consider using local data (if it exists)
        //console.log('[fullDataSyncHandler] No conversation data found on server');
        
        if (hasLocalData) {
          //console.log('[fullDataSyncHandler] Using existing local data since server has none');
        } else {
          //console.log('[fullDataSyncHandler] No data found on server or locally');
        }
      }
      
      // 4. For location data, SERVER IS ALSO SOURCE OF TRUTH
      if (serverLocations?.length > 0) {
        dataSyncService.updateLocationCards(transformers.locationsApiToClient(serverLocations));
        //console.log(`[fullDataSyncHandler] Imported ${serverLocations.length} locations from server`);
      }
      
      // Similar approach for other data types...
      
      // Remove the in-progress flag when done
      localStorage.removeItem('server_data_fetch_in_progress');
      
      // Dispatch event to notify that server data has been loaded
      window.dispatchEvent(new CustomEvent('tripper-server-data-loaded', {
        detail: { dataImported, hasServerData: dataImported }
      }));
      
      //console.log('[fullDataSyncHandler] Server data initialization complete, imported: ', dataImported);
      return true;
    } catch (error) {
      //console.error('[fullDataSyncHandler] Failed to initialize from server:', error);
      // Remove the in-progress flag in case of error
      localStorage.removeItem('server_data_fetch_in_progress');
      return false;
    }
  }

  // Helper to fetch from server
  private async fetchFromServer(endpoint: string): Promise<any> {
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authTokenManager.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(`[fullDataSyncHandler] Error fetching from ${endpoint}:`, error);
      return null;
    }
  }
  
  /**
   * Get current sync status
   */
  public getSyncStatus(): SyncState {
    // Determine overall status
    let overall: 'idle' | 'syncing' | 'error' | 'success' = 'idle';
    let error: string | null = null;
    
    if (this.syncInProgress) {
      overall = 'syncing';
    } else if (Object.values(this.syncStatus).some(s => s.status === 'error')) {
      overall = 'error';
      // Find the first error message
      const errorEntry = Object.values(this.syncStatus).find(s => s.status === 'error');
      if (errorEntry && errorEntry.error) {
        error = errorEntry.error;
      }
    } else if (Object.keys(this.syncStatus).length > 0) {
      overall = 'success';
    }
    
    return {
      status: overall,
      lastSyncTime: this.lastSyncTime,
      error
    };
  }
  
  /**
   * Add a sync status listener
   */
  public addStatusListener(listener: (state: SyncState) => void): void {
    autoSyncService.addSyncListener(listener);
  }
  
  /**
   * Remove a sync status listener
   */
  public removeStatusListener(listener: (state: SyncState) => void): void {
    autoSyncService.removeSyncListener(listener);
  }
  
  /**
   * Check if network is available
   */
  public isOnline(): boolean {
    return navigator.onLine;
  }
  

  // Data freshness check
  private shouldSyncData(localData: any, serverData: any): boolean {
    // If local data has no lastSyncTime, it's never been synced
    if (!localData.lastSyncTime) {
      return true;
    }
    
    // Only sync if local data is newer than what was last synced
    return localData.lastUpdatedAt > localData.lastSyncTime;
  }
  

  // Data completeness check
  private isLocalDataComplete(localData: any, serverData: any): boolean {
    // Don't sync if local data is empty but server has data
    if (this.isEmpty(localData) && !this.isEmpty(serverData)) {
      console.log("[SyncService] Preventing sync of empty local data over populated server data");
      return false;
    }
    
    // For collections, check item counts
    if (Array.isArray(localData) && Array.isArray(serverData)) {
      // Only sync if local has at least as many items as server
      return localData.length >= serverData.length;
    }
    
    return true;
  }

  // Check if local data enhances server data
  private hasAdditionalData(localData: any, serverData: any): boolean {
    // If server has no data, any local data is additional
    if (this.isEmpty(serverData)) {
      return !this.isEmpty(localData);
    }
    
    // For objects like conversation or itinerary data
    if (typeof localData === 'object' && !Array.isArray(localData)) {
      return Object.keys(localData).some(key => {
        // New property or updated content
        if (!serverData[key]) return true;
        return JSON.stringify(localData[key]) !== JSON.stringify(serverData[key]);
      });
    }
    
    // For arrays (like messages or locations)
    if (Array.isArray(localData) && Array.isArray(serverData)) {
      // Check if local array has additional items
      if (localData.length > serverData.length) return true;
      
      // Check for updated items
      return localData.some((item, index) => {
        const serverId = serverData[index]?.id;
        return item.id !== serverId || JSON.stringify(item) !== JSON.stringify(serverData[index]);
      });
    }
    
    return false;
  }

  // Helper to check if data is empty
  private isEmpty(data: any): boolean {
    if (data == null) return true;
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data === 'object') return Object.keys(data).length === 0;
    return false;
  }

  
  // Resolve conflicts between local and server data
  private resolveConflicts(localData: any, serverData: any, dataType: string): any {
    // For conversation data, merge messages preserving both sides
    if (dataType === 'conversation') {
      const mergedMessages = this.mergeBothMessageArrays(
        localData.sequence || [], 
        serverData.sequence || []
      );
      
      return {
        ...serverData,
        sequence: mergedMessages,
        lastUpdatedAt: Math.max(localData.lastUpdatedAt || 0, serverData.lastUpdatedAt || 0)
      };
    }
    
    // For other types, prioritize most recent data
    return localData.lastUpdatedAt > serverData.lastUpdatedAt ? localData : serverData;
  }
  

  // Helper to merge message arrays without duplicates
  private mergeBothMessageArrays(localMessages: any[], serverMessages: any[]): any[] {
    const messageMap = new Map();
    
    // Add all server messages to map
    serverMessages.forEach(msg => messageMap.set(msg.id, msg));
    
    // Add local messages that don't exist in server
    localMessages.forEach(msg => {
      if (!messageMap.has(msg.id)) {
        messageMap.set(msg.id, msg);
      }
    });
    
    // Convert map back to array and sort by timestamp
    return Array.from(messageMap.values())
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  // Fix 5: Update syncAllData to prevent overwriting good server data with empty local data
  public async syncAllData(): Promise<boolean> {
    // Safety check: Don't sync during server data fetch
    if (localStorage.getItem('server_data_fetch_in_progress') === 'true') {
      console.log('[fullDataSyncHandler] Cannot sync while server data fetch is in progress');
      return false;
    }

    if (this.syncInProgress || !navigator.onLine) {
      console.log('[fullDataSyncHandler] Cannot sync: ' + 
        (this.syncInProgress ? 'sync already in progress' : 'offline'));
      return false;
    }
    
    this.syncInProgress = true;
    this.updateSyncStatus('all', 'syncing');
    
    try {
      // First, get current server data to compare with local
      console.log('[fullDataSyncHandler] Checking server data before sync');
      
      const [serverConversation, serverLocations] = await Promise.all([
        this.fetchFromServer('/api/user-data/conversation'),
        this.fetchFromServer('/api/user-data/locations')
      ]);
      
      const hasServerConversation = serverConversation?.sequence?.length > 0;
      const hasServerLocations = serverLocations?.length > 0;
      
      // Get all data from local storage
      const userData = dataSyncService.getAllData();
      
      // VALIDATION: Check for empty data that would overwrite server data
      const hasLocalConversation = userData.conversation?.sequence?.length > 0;
      const hasLocalLocations = userData.locations?.cards?.length > 0;
      const hasLocalContent = hasLocalConversation || hasLocalLocations;
      
      // CRITICAL SAFETY CHECK: Don't overwrite good server data with empty local data
      if (!hasLocalContent && (hasServerConversation || hasServerLocations)) {
        console.warn('[fullDataSyncHandler] Prevented sync of empty local data that would overwrite server data');
        this.updateSyncStatus('all', 'error', 'Prevented overwrite of server data with empty data');
        this.syncInProgress = false;
        
        // Fetch server data again to ensure we have it locally
        this.initializeFromServer();
        return false;
      }
      
      // If local has data but server has more, prioritize server
      if (hasServerConversation && hasLocalConversation) {
        const serverCount = serverConversation.sequence.length;
        const localCount = userData.conversation.sequence.length;
        
        if (serverCount > localCount) {
          console.warn(`[fullDataSyncHandler] Server has more conversation data (${serverCount} vs ${localCount}). Fetching server data first.`);
          await this.initializeFromServer();
          // Get updated local data after server sync
          const updatedUserData = dataSyncService.getAllData();
          userData.conversation = updatedUserData.conversation;
        }
      }
      
      console.log('[fullDataSyncHandler] Validating data before sync:', {
        conversationLength: userData.conversation?.sequence?.length || 0,
        locationsLength: userData.locations?.cards?.length || 0,
        weatherLength: userData.weather?.widgets?.length || 0,
        imagesLength: userData.images?.messages?.length || 0
      });

      // Continue with original sync operations using the validated data
      const results = await Promise.allSettled([
        // Only sync data types that have content and passed validation
        (userData.conversation?.sequence?.length ?? 0) > 0 ? 
          this.syncConversation(userData) : Promise.resolve(true),
          
        (userData.locations?.cards?.length ?? 0) > 0 ? 
          this.syncLocations(userData) : Promise.resolve(true),

        (userData.images?.messages?.length ?? 0) > 0 ? 
          this.syncImages(userData) : Promise.resolve(true),
        
        userData.mapState ?
          this.syncMapState(userData) : Promise.resolve(true),
          
        userData.preferences ?
          this.syncPreferences(userData) : Promise.resolve(true),  
        
        (userData.weather?.widgets?.length ?? 0) > 0 ?
          this.syncWeather(userData) : Promise.resolve(true)  
          
        // Other data types...
      ]);
      
      // Check results
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        console.error(`[fullDataSyncHandler] ${failed.length} sync operations failed`);
        this.updateSyncStatus('all', 'error', 'Some sync operations failed');
        return false;
      }
      
      // Update last sync time
      this.lastSyncTime = Date.now();
      this.updateSyncStatus('all', 'success');
      
      console.log('[fullDataSyncHandler] All data synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing all data:', error);
      this.updateSyncStatus('all', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    } finally {
      this.syncInProgress = false;
    }
  }
  
  

  /**
   * Check if sync is possible
   */
  private canSync(): boolean {
    // Check if user is authenticated
    const isAuthenticated = !!localStorage.getItem('authToken');
    
    console.log('[FullDataSyncHandler] canSync check:', {
      isAuthenticated,
      isNetworkOnline: navigator.onLine,
      syncInProgress: this.syncInProgress
    });
    
    return isAuthenticated && 
          navigator.onLine && 
          !this.syncInProgress;
  }

  /**
   * Sync itineraries with the server
   */
  private async syncItineraries(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('itineraries', 'syncing');
    
    try {
      console.log('[fullDataSyncHandler] Syncing itineraries...');
      
      // First get all itineraries from server
      const serverResponse = await itineraryApiService.getAllItineraries();
      
      if (!serverResponse.success) {
        throw new Error(serverResponse.error || 'Failed to fetch itineraries');
      }
      
      // Local itineraries
      const localItineraries = userData.itineraries?.items || [];
      
      // Server itineraries
      const serverItineraries = serverResponse.data || [];
      
      // Create maps for easier lookup
      const localMap = new Map(localItineraries.map(i => [i.id, i]));
      const serverMap = new Map(serverItineraries.map(i => [i.id, i]));
      
      // Track operations to perform
      const toCreate: any[] = [];
      const toUpdate: any[] = [];
      const toSyncLocally: any[] = [];
      
      // Determine operations to perform
      for (const localItem of localItineraries) {
        const serverId = localItem.id.startsWith('itinerary_') 
          ? null  // Local temporary ID
          : localItem.id;
          
        const serverItem = serverId ? serverMap.get(serverId) : null;
        
        if (!serverItem) {
          // Item exists locally but not on server - create it
          toCreate.push(localItem);
        } else {
          // Item exists in both - check which is newer
          const localUpdated = new Date(localItem.updatedAt).getTime();
          const serverUpdated = new Date(serverItem.updatedAt).getTime();
          
          if (localUpdated > serverUpdated) {
            // Local is newer - update server
            toUpdate.push(localItem);
          } else if (serverUpdated > localUpdated) {
            // Server is newer - update local
            toSyncLocally.push(serverItem);
          }
        }
      }
      
      // Find server items that don't exist locally
      for (const serverItem of serverItineraries) {
        if (!localMap.has(serverItem.id)) {
          // Item exists on server but not locally - sync to local
          toSyncLocally.push(serverItem);
        }
      }
      
      // Process create operations
      const createResults = await Promise.allSettled(
        toCreate.map(item => itineraryApiService.createItinerary(item))
      );
      
      // Process update operations
      const updateResults = await Promise.allSettled(
        toUpdate.map(item => itineraryApiService.updateItinerary(item.id, item))
      );
      
      // Update local data with server data
      if (toSyncLocally.length > 0) {
        // Update local store by merging with current data
        const currentItems = userData.itineraries?.items || [];
        const currentMap = new Map(currentItems.map(i => [i.id, i]));
        
        // Merge server items
        for (const serverItem of toSyncLocally) {
          currentMap.set(serverItem.id, serverItem);
        }
        
        // Update data store
        dataSyncService.updateItineraries({
          items: Array.from(currentMap.values()),
          currentItineraryId: userData.itineraries?.currentItineraryId
        });
      }
      
      // Check for errors
      const failed = [
        ...createResults.filter(r => r.status === 'rejected'),
        ...updateResults.filter(r => r.status === 'rejected')
      ];
      
      if (failed.length > 0) {
        console.error(`[fullDataSyncHandler] ${failed.length} itinerary operations failed`);
        this.updateSyncStatus('itineraries', 'error', `${failed.length} operations failed`);
        return false;
      }
      
      this.updateSyncStatus('itineraries', 'success');
      console.log('[fullDataSyncHandler] Itineraries synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing itineraries:', error);
      this.updateSyncStatus('itineraries', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }
  
  /**
   * Sync conversation data with the server
   */
/*   private async syncConversation(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('conversation', 'syncing');
    
    console.log('[fullDataSyncHandler] Syncing conversation...',userData);
    try {
      // Get conversation data
      const conversationData = userData.conversation;

      console.log('[fullDataSyncHandler] Conversation data...',conversationData);
      
      if (!conversationData) {
        console.log('[fullDataSyncHandler] No conversation data to sync');
        this.updateSyncStatus('conversation', 'success');
        return true;
      }
      
      // Store conversation
      const response = await conversationApiService.saveConversation(conversationData);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save conversation');
      }
      
      this.updateSyncStatus('conversation', 'success');
      console.log('[fullDataSyncHandler] Conversation synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing conversation:', error);
      this.updateSyncStatus('conversation', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  } */
  // Modify syncConversation method to add change detection
/*   private async syncConversation(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('conversation', 'checking');
    
    try {
      // Get conversation data
      const conversationData = userData.conversation;
      
      if (!conversationData || !conversationData.sequence || conversationData.sequence.length === 0) {
        console.log('[fullDataSyncHandler] No conversation data to sync');
        this.updateSyncStatus('conversation', 'success');
        return true;
      }
      
      // Get only changed conversation elements
      const changedIds = dataSyncService.getChangedEntityIds('conversation');
      
      if (changedIds.size === 0) {
        console.log('[fullDataSyncHandler] No conversation changes to sync');
        this.updateSyncStatus('conversation', 'success');
        return true;
      }
      
      // Extract only the changed elements
      const changedElements = conversationData.sequence.filter(
        element => changedIds.has(element.elementId)
      );
      
      console.log(`[fullDataSyncHandler] Syncing ${changedElements.length} conversation elements`);
      this.updateSyncStatus('conversation', 'syncing');
      
      // Store only changed elements
      const response = await conversationApiService.saveConversationElements(changedElements);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save conversation elements');
      }
      
      // Clear the changed tracking after successful sync
      dataSyncService.clearChangedEntityIds('conversation', changedIds);
      
      this.updateLastSyncTime('conversation');
      this.updateSyncStatus('conversation', 'success');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing conversation:', error);
      this.updateSyncStatus('conversation', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  } */

/*     private async syncConversation(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('conversation', 'checking');
    
    try {
      // Get conversation data
      const conversationData = userData.conversation;
      
      if (!conversationData || !conversationData.sequence || conversationData.sequence.length === 0) {
        console.log('[fullDataSyncHandler] No conversation data to sync');
        this.updateSyncStatus('conversation', 'success');
        return true;
      }
      
      // Get only changed conversation elements
      const changedIds = dataSyncService.getChangedEntityIds('conversation');
      
      if (changedIds.size === 0) {
        console.log('[fullDataSyncHandler] No conversation changes to sync');
        this.updateSyncStatus('conversation', 'success');
        return true;
      }
      
      // Extract only the changed elements
      const changedElements = conversationData.sequence.filter(
        element => changedIds.has(element.elementId)
      );
      
      console.log(`[fullDataSyncHandler] Syncing ${changedElements.length} conversation elements`);
      this.updateSyncStatus('conversation', 'syncing');
      
      // Store only changed elements
      const response = await conversationApiService.saveConversation(changedElements);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save conversation elements');
      }
      
      // Clear the changed tracking after successful sync
      dataSyncService.clearChangedEntityIds('conversation', changedIds);
      
      this.updateLastSyncTime('conversation');
      this.updateSyncStatus('conversation', 'success');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing conversation:', error);
      this.updateSyncStatus('conversation', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  } */

  private async syncConversation(userData: TripperUserData): Promise<boolean> {

    console.log('[fullDataSyncHandler] Syncing conversation.................. ATLEAST IM IN CONSOLE -------------------------------- ', userData);
    this.updateSyncStatus('conversation', 'checking');
    try {
      const conversationData = userData.conversation;
      if (!conversationData?.sequence?.length) {
        this.updateSyncStatus('conversation', 'success');
        return true;
      }
      
      // IMPORTANT DEBUG LOG - Add this
      const changedIds = dataSyncService.getChangedEntityIds('conversation');
      console.log('[DEBUG] Delta sync:', {
        trackingEnabled: true,
        changedIdsCount: changedIds.size,
        changedIds: Array.from(changedIds)
      });
      
      // Only use delta if we have changes
      if (changedIds.size > 0) {
        console.log(`[DEBUG] Using delta sync for ${changedIds.size} elements`);
        const changedElements = conversationData.sequence.filter(
          element => changedIds.has(element.elementId)
        );
        
        const response = await conversationApiService.saveConversationElements(changedElements);
        
        if (response.success) {
          dataSyncService.clearChangedEntityIds('conversation', changedIds);
          this.updateLastSyncTime('conversation');
          this.updateSyncStatus('conversation', 'success');
          return true;
        }
      }
      
      // Fallback to full sync if delta fails or no changes tracked
      console.log('[DEBUG] Falling back to full sync');
      return await this.legacySyncConversation(userData);
    } catch (error) {
      console.error('[DEBUG] Error in delta sync:', error);
      return await this.legacySyncConversation(userData);
    }
  }
  
  // Add legacy method
  private async legacySyncConversation(userData: TripperUserData): Promise<boolean> {
    console.log('[fullDataSyncHandler] Syncing conversation.................. ATLEAST IM IN THIS CONSOLE -------------------------------- ', userData);
    try {
      const response = await conversationApiService.saveConversation(userData.conversation);
      if (!response.success) {
        throw new Error(response.error || 'Failed to save conversation');
      }
      this.updateSyncStatus('conversation', 'success');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error in legacy sync:', error);
      this.updateSyncStatus('conversation', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }

    /**
   * Sync location data with the server
   */
  /* private async syncLocations(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('locations', 'syncing');
    
    try {
      // Get location data
      const locationCards = userData.locations?.cards || [];
      
      if (locationCards.length === 0) {
        console.log('[fullDataSyncHandler] No location data to sync');
        this.updateSyncStatus('locations', 'success');
        return true;
      }
      
      // Store locations
      const response = await locationApiService.saveLocations(locationCards);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save locations');
      }
      
      this.updateSyncStatus('locations', 'success');
      console.log('[fullDataSyncHandler] Locations synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing locations:', error);
      this.updateSyncStatus('locations', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  } */
  // Apply similar change detection to other sync methods (locations, weather, etc.)
  // Example for syncLocations:
  private async syncLocations(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('locations', 'checking');
    
    try {
      // Get location data
      const locationCards = userData.locations?.cards || [];
      
      if (locationCards.length === 0) {
        console.log('[fullDataSyncHandler] No location data to sync');
        this.updateSyncStatus('locations', 'success');
        return true;
      }
      
      // OPTIMIZATION: Only sync if data has changed since last sync
      if (!this.hasDataChanged('locations', locationCards)) {
        console.log('[fullDataSyncHandler] Location data unchanged since last sync, skipping');
        this.updateSyncStatus('locations', 'success');
        return true;
      }
      
      // Data has changed, proceed with sync
      console.log('[fullDataSyncHandler] Location data has changed, syncing');
      this.updateSyncStatus('locations', 'syncing');
      
      // Store locations
      const response = await locationApiService.saveLocations(locationCards);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save locations');
      }
      
      // Update last sync time for this data type
      this.updateLastSyncTime('locations');
      
      this.updateSyncStatus('locations', 'success');
      console.log('[fullDataSyncHandler] Locations synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing locations:', error);
      this.updateSyncStatus('locations', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }

  /**
   * Sync weather data with the server
   */
  private async syncWeather(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('weather', 'syncing');
    
    try {
      // Get weather data
      const weatherWidgets = userData.weather?.widgets || [];
      
      if (weatherWidgets.length === 0) {
        console.log('[fullDataSyncHandler] No weather data to sync');
        this.updateSyncStatus('weather', 'success');
        return true;
      }
      
      // Store weather
      const response = await weatherApiService.saveWeatherData(weatherWidgets);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save weather data');
      }
      
      this.updateSyncStatus('weather', 'success');
      console.log('[fullDataSyncHandler] Weather data synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing weather data:', error);
      this.updateSyncStatus('weather', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }
  
  /**
   * Sync image data with the server
   */
  private async syncImages_UNUSED(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('images', 'syncing');
    
    try {
      // Get image data
      const imageMessages = userData.images?.messages || [];
      
      if (imageMessages.length === 0) {
        console.log('[fullDataSyncHandler] No image data to sync');
        this.updateSyncStatus('images', 'success');
        return true;
      }
      
      // Store images
      const response = await imageApiService.saveImages(imageMessages);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save images');
      }
      
      this.updateSyncStatus('images', 'success');
      console.log('[fullDataSyncHandler] Images synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing images:', error);
      this.updateSyncStatus('images', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }

  private async syncImages(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('images', 'syncing');
    
    try {
      // Get image data
      const imageMessages = userData.images?.messages || [];
      
      console.log('[syncImages] Starting sync, images count:', imageMessages.length);
      
      if (imageMessages.length === 0) {
        console.log('[fullDataSyncHandler] No image data to sync');
        this.updateSyncStatus('images', 'success');
        return true;
      }
      
      // Get changed IDs
      const changedIds = dataSyncService.getChangedEntityIds('images');
      console.log('[syncImages] Changed IDs:', Array.from(changedIds));
      
      if (changedIds.size === 0) {
        console.log('[syncImages] No changed images to sync');
        this.updateSyncStatus('images', 'success');
        return true;
      }
      
      // Filter only changed images
      const changedImages = imageMessages.filter(img => changedIds.has(img.id));
      console.log('[syncImages] Sending images:', changedImages);
      
      // Store images
      const response = await imageApiService.saveImages(changedImages);
      console.log('[syncImages] API Response:', response);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save images');
      }
      
      // Clear changed tracking
      dataSyncService.clearChangedEntityIds('images', changedIds);
      
      this.updateSyncStatus('images', 'success');
      console.log('[fullDataSyncHandler] Images synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing images:', error);
      this.updateSyncStatus('images', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }
  
  /**
   * Sync map state with the server
   */
  private async syncMapState(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('mapState', 'syncing');
    
    try {
      // Get map state
      const mapState = userData.mapState;
      
      if (!mapState) {
        console.log('[fullDataSyncHandler] No map state to sync');
        this.updateSyncStatus('mapState', 'success');
        return true;
      }
      
      // Store map state
      const response = await mapStateApiService.saveMapState(mapState);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save map state');
      }
      
      this.updateSyncStatus('mapState', 'success');
      console.log('[fullDataSyncHandler] Map state synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing map state:', error);
      this.updateSyncStatus('mapState', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }
  
  /**
   * Sync preferences with the server
   */
  private async syncPreferences(userData: TripperUserData): Promise<boolean> {
    this.updateSyncStatus('preferences', 'syncing');
    
    try {
      // Get preferences
      const preferences = userData.preferences;
      
      if (!preferences) {
        console.log('[fullDataSyncHandler] No preferences to sync');
        this.updateSyncStatus('preferences', 'success');
        return true;
      }
      
      // Store preferences
      const response = await preferencesApiService.savePreferences(preferences);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to save preferences');
      }
      
      this.updateSyncStatus('preferences', 'success');
      console.log('[fullDataSyncHandler] Preferences synced successfully');
      return true;
    } catch (error) {
      console.error('[fullDataSyncHandler] Error syncing preferences:', error);
      this.updateSyncStatus('preferences', 'error', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }
}

// Export singleton instance
export const fullDataSyncHandler = new FullDataSyncHandlerClass();